import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { REMOTE_FEATURES, REMOTE_FEATURE_LABELS } from '@ids-constants';
import { BaseComponent } from '@ids-components';
import { TargetDeviceService } from '@ids-services';
import { ActionMenuItem } from '@microsec/models';

import { finalize, forkJoin } from 'rxjs';

import { MicroagentConfigAnomalyDetectionFormComponent } from './microagent-config-anomaly-detection-form/microagent-config-anomaly-detection-form.component';
import { MicroagentConfigAssessmentFormComponent } from './microagent-config-assessment-form/microagent-config-assessment-form.component';
import { MicroagentConfigActiveScanFormComponent } from './microagent-config-active-scan-form/microagent-config-active-scan-form.component';
import { MicroagentConfigFileIntegrityFormComponent } from './microagent-config-file-integrity-form/microagent-config-file-integrity-form.component';
import { MicroagentConfigPcapFormComponent } from './microagent-config-pcap-form/microagent-config-pcap-form.component';
import { MOMENT_DATETIME } from '@microsec/constants';
import moment from 'moment';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CheckboxModule } from 'primeng/checkbox';
import { CardComponent, CommonTableComponent, RefreshButtonComponent } from '@microsec/components';

@Component({
  selector: 'app-device-agent-config',
  templateUrl: './device-agent-config.component.html',
  styleUrls: ['./device-agent-config.component.scss'],
  imports: [CommonModule, FormsModule, CheckboxModule, CardComponent, CommonTableComponent, RefreshButtonComponent],
  providers: [
    MicroagentConfigAnomalyDetectionFormComponent,
    MicroagentConfigAssessmentFormComponent,
    MicroagentConfigActiveScanFormComponent,
    MicroagentConfigPcapFormComponent,
    MicroagentConfigFileIntegrityFormComponent,
  ],
})
export class DeviceAnomalyDetectionComponent extends BaseComponent implements OnInit, OnDestroy {
  isLoading = false;

  _deviceId = false;

  get deviceId() {
    return this._deviceId;
  }

  @Input() set deviceId(value: any) {
    this._deviceId = value;
    this.getDeviceConfiguration();
  }

  @Input() deviceLastSeen = null;

  interfaceOptions: any[] = [];

  configuration: any = [];

  cols: any[] = [
    { header: 'Feature', field: 'featureLabel', width: 20 },
    { header: 'Enabled', field: 'enabled', width: 8 },
    { header: 'Status', field: 'status', width: 10 },
    { header: 'Progress', field: 'progress', width: 10 },
  ];

  values: any[] = [];

  anomalyDetectionFeatures = Object.values(REMOTE_FEATURES).filter(
    (feature) => feature !== REMOTE_FEATURES.PACKAGE_HANDLER && feature !== REMOTE_FEATURES.FIREWALL_HANDLER,
  );

  actionsMenuItems: ActionMenuItem[] = [];

  constructor(private targetDeviceSrv: TargetDeviceService) {
    super();
  }

  async ngOnInit() {
    await this.prepareConfigs();
    this.actionsMenuItems = [
      {
        label: 'Configuration',
        icon: 'fa fa-cog',
        visible: ({ rowData }) =>
          !!(
            rowData.feature === REMOTE_FEATURES.CAN_NETWORK_ANALYZER ||
            rowData.feature === REMOTE_FEATURES.METRIC_ANALYZER ||
            rowData.feature === REMOTE_FEATURES.ACTIVE_SCAN ||
            rowData.feature === REMOTE_FEATURES.PCAP_CAPTURE ||
            rowData.feature === REMOTE_FEATURES.DEVICE_ASSESSMENT ||
            rowData.feature === REMOTE_FEATURES.FILE_INTEGRITY_ANALYZER
          ),
        command: ({ rowData }) => this.openConfigurationForm(rowData.feature),
      },
      {
        label: 'Retrain',
        icon: 'fa fa-repeat',
        visible: ({ rowData }) => !!(rowData.feature === REMOTE_FEATURES.CAN_NETWORK_ANALYZER || rowData.feature === REMOTE_FEATURES.METRIC_ANALYZER),
      },
    ];
    this.getDeviceConfiguration();
    this.subscriptions.forEach((s) => s.unsubscribe());
    const subscription = this.targetDeviceSrv.refreshObs.subscribe((rs) => {
      if (!!rs) {
        this.getDeviceConfiguration();
      }
    });
    this.subscriptions.push(subscription);
  }

  getDeviceConfiguration() {
    if (!!this.deviceId) {
      this.isLoading = true;
      const deviceConfiguration: any = {};
      forkJoin({
        config: this.targetDeviceSrv.getDeviceConfiguration(this.deviceId),
        interfaces: this.targetDeviceSrv.getDeviceMetrics({
          deviceId: this.deviceId,
          granularity: null,
          noDateRange: false,
          fromDate: moment.utc(this.deviceLastSeen).local().subtract(1, 'minute').local().format(MOMENT_DATETIME),
        }),
      })
        .pipe(
          finalize(() => {
            this.isLoading = false;
          }),
        )
        .subscribe({
          next: (res: any) => {
            // Set the Device Configuration
            this.configuration = res.config;
            // Only values here will be shown in the table
            const availableFeatures: any[] = ((res.config?.enabled_features as any[]) || []).filter((feature) => {
              return (
                feature !== REMOTE_FEATURES.FIREWALL_HANDLER &&
                feature !== REMOTE_FEATURES.PACKAGE_HANDLER &&
                feature !== REMOTE_FEATURES.MALWARE_ANALYZER
              );
            });
            availableFeatures.forEach((feature) => {
              let modifiedFeature = feature;
              if (feature === 'device_assessment') {
                modifiedFeature = REMOTE_FEATURES.DEVICE_ASSESSMENT;
              }

              deviceConfiguration[modifiedFeature] = {
                feature: modifiedFeature,
                featureLabel: REMOTE_FEATURE_LABELS[modifiedFeature] || modifiedFeature,
                enabled: this.getCustomEnableValue(res.config?.configuration, modifiedFeature),
                status: this.getCustomStatusValue(res.config?.configuration, modifiedFeature),
                progress: this.getCustomProgressValue(res.config?.configuration, modifiedFeature),
              };
            });
            this.values = Object.values(deviceConfiguration);

            // Set the Interface Options
            const interfaces = res.interfaces.data?.[0]?.interfaces || [];
            this.interfaceOptions = interfaces.map((inf: any) => {
              const infValue = Object.keys(inf)[0];
              return { label: infValue, value: infValue };
            });
          },
          error: (error) => {
            this.showErrorMessage(error);
          },
        });
    }
  }

  openConfigurationForm(feature: string) {
    const formComponent: any = this.getFeatureFormComponent(feature);

    const dialog = this.dialogSrv.open(formComponent, {
      header: `${REMOTE_FEATURE_LABELS[feature] || feature} Configuration`,
      data: {
        deviceId: this.deviceId,
        configuration: this.configuration?.configuration || {},
        feature: feature,
        interfaceOptions: this.interfaceOptions,
      },
      width: '800px',
      height: 'min-content',
      closable: true,
    });
    dialog.onClose.subscribe((rs) => {
      if (!!rs) {
        this.getDeviceConfiguration();
      }
    });
  }

  getCustomEnableValue(config: any, feature: string): boolean {
    const name = `${feature}_enabled`;
    if (feature === REMOTE_FEATURES.ACTIVE_SCAN) {
      return config[feature]?.[`config_${name}`] === 'true';
    }
    if (
      feature === REMOTE_FEATURES.METRIC_ANALYZER ||
      feature === REMOTE_FEATURES.CAN_NETWORK_ANALYZER ||
      feature === REMOTE_FEATURES.ETH_NETWORK_ANALYZER
    ) {
      return config['anomaly_detection']?.[feature]?.[name] === 'true';
    }
    return config[feature]?.[name] === 'true';
  }

  getCustomStatusValue(config: any, feature: string): string {
    if (feature === REMOTE_FEATURES.ACTIVE_SCAN) {
      return ((config[feature]?.status as string) ?? '').replace('_', ' ') || '-';
    } else if (feature === REMOTE_FEATURES.METRIC_ANALYZER || feature === REMOTE_FEATURES.CAN_NETWORK_ANALYZER) {
      return config['anomaly_detection']?.[feature]?.['_mode'] || 'predicting';
    } else {
      return this.getCustomEnableValue(config, feature) ? 'enabled' : 'disabled';
    }
  }

  getCustomProgressValue(config: any, feature: string): string {
    if (feature === REMOTE_FEATURES.METRIC_ANALYZER || feature === REMOTE_FEATURES.CAN_NETWORK_ANALYZER) {
      const anomaly = config['anomaly_detection']?.[feature];
      const isAnomalyStatePredicting: boolean = anomaly?.['_mode'] === 'predicting';
      if (!isAnomalyStatePredicting) {
        if (feature === REMOTE_FEATURES.METRIC_ANALYZER) {
          return `${anomaly._datapoints_current} / ${anomaly.parameters?.metric_num_datapoints_to_train}`;
        }
        if (feature === REMOTE_FEATURES.CAN_NETWORK_ANALYZER) {
          return `${anomaly._datapoints_current} / ${anomaly.parameters?.network_num_datapoints_svm}`;
        }
      }
      return '-';
    }
    return '-';
  }

  handleToggleEnable(event: any, data: any) {
    const featureEnableObj = {
      [data.feature]:
        data.feature === REMOTE_FEATURES.ACTIVE_SCAN
          ? { [`config_${data.feature}_enabled`]: String(event) }
          : { [`${data.feature}_enabled`]: String(event) },
    };
    const payload = {
      configuration: {
        ...(data.feature === REMOTE_FEATURES.CAN_NETWORK_ANALYZER ||
        data.feature === REMOTE_FEATURES.METRIC_ANALYZER ||
        data.feature === REMOTE_FEATURES.ETH_NETWORK_ANALYZER
          ? { anomaly_detection: featureEnableObj }
          : featureEnableObj),
      },
    };
    this.targetDeviceSrv
      .updateDeviceConfiguration(this.deviceId, payload)
      .pipe(
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe({
        next: () => {
          this.showSuccessMessage(`Updated enabled status for ${data.featureLabel} to ${event} successfully`);
          this.getDeviceConfiguration();
        },
        error: (err: any) => {
          this.showErrorMessage(err);
        },
      });
  }

  getFeatureFormComponent(feature: string) {
    switch (feature) {
      case REMOTE_FEATURES.CAN_NETWORK_ANALYZER: {
        return MicroagentConfigAnomalyDetectionFormComponent;
      }
      case REMOTE_FEATURES.METRIC_ANALYZER: {
        return MicroagentConfigAnomalyDetectionFormComponent;
      }
      case REMOTE_FEATURES.DEVICE_ASSESSMENT: {
        return MicroagentConfigAssessmentFormComponent;
      }
      case REMOTE_FEATURES.ACTIVE_SCAN: {
        return MicroagentConfigActiveScanFormComponent;
      }
      case REMOTE_FEATURES.PCAP_CAPTURE: {
        return MicroagentConfigPcapFormComponent;
      }
      case REMOTE_FEATURES.FILE_INTEGRITY_ANALYZER: {
        return MicroagentConfigFileIntegrityFormComponent;
      }
      default: {
        return MicroagentConfigAnomalyDetectionFormComponent;
      }
    }
  }
}
